import React from 'react'
import { useLocation, NavLink, useRouteMatch } from 'react-router-dom'

import {
  routes,
  useFlag,
  useGetNewNotificationsCount,
  useUserProfile,
  useSignOut,
  isWorkspaceSettingsRoute,
  isPersonalSettingsRoute,
  isUpdatesRoute,
} from '@sketch/modules-common'

import { ReactComponent as KeyIcon } from '@sketch/icons/key-8'
import { ReactComponent as SignOutIcon } from '@sketch/icons/arrow-right-rectangle-17'
import { ReactComponent as PersonIcon } from '@sketch/icons/person-17'
import { ReactComponent as BellIcon } from '@sketch/icons/bell-24'
import { ReactComponent as GearIcon } from '@sketch/icons/gear-24'

import {
  Link,
  useResponsiveDropdown,
  Tooltip,
  useBreakpoint,
} from '@sketch/components'

import { AppearanceMenu } from './AppearanceMenu'

import {
  DropdownHeader,
  UserName,
  StyledDropdownItemNavLink,
  StyledDropdownItem,
  Icon,
  Button,
  Avatar,
  Pill,
  DropdownUserName,
  DropdownUserEmail,
  KeyWrapper,
  SidebarHeaderRight,
  NavLinkWrapper,
} from './UserDropdown.styles'

import {
  useGetCustomerSignupSourceQuery,
  UserFragment,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { isAppStoreWorkspace } from '../../../../workspace/utils'

const DROPDOWN_STYLE = {
  width: '216px',
}

const formatUnreadNotifications = (unreadCounter: number) => {
  return unreadCounter > 99 ? '99+' : `${unreadCounter}`
}

interface NotificationsPillProps {
  unreadNotifications: number
  withNumberDisplay: boolean
}

export const NotificationsPill = ({
  unreadNotifications,
  withNumberDisplay,
}: NotificationsPillProps) => {
  const formattedNotification = formatUnreadNotifications(unreadNotifications)
  return unreadNotifications > 0 ? (
    <Pill
      data-testid="user-unseen-notifications"
      withNumberDisplay={withNumberDisplay}
    >
      {withNumberDisplay ? (
        formattedNotification
      ) : (
        <span className="sr-only">{formattedNotification}</span>
      )}
    </Pill>
  ) : null
}

interface DropdownContentProps {
  name: string
  email: string
  unreadNotifications: number
}

const DropdownContent = (props: DropdownContentProps) => {
  const { name, email } = props

  const isDarkModeToggleEnabled = useFlag('dark-mode-toggle')

  const signOut = useSignOut({
    redirectBackAfterLoginAgain: false,
    reason: 'Logout button',
  })

  return (
    <>
      <DropdownHeader>
        <DropdownUserName>{name}</DropdownUserName>
        <DropdownUserEmail>{email}</DropdownUserEmail>
      </DropdownHeader>

      <StyledDropdownItemNavLink
        icon={<PersonIcon />}
        to={routes.PERSONAL_SETTINGS.create({})}
      >
        Account
      </StyledDropdownItemNavLink>

      {isDarkModeToggleEnabled && <AppearanceMenu />}

      <StyledDropdownItem icon={<SignOutIcon />} onClick={() => signOut()}>
        Sign Out
      </StyledDropdownItem>
    </>
  )
}

interface AuthenticatedUserDropdownProps {
  className?: string
  user: UserFragment
  unreadNotifications: number
  workspace?: WorkspaceMinimalFragment
}

const determineSettingsRoute = (
  hasWorkspaces: boolean,
  isGuest: boolean,
  isAdmin: boolean,
  isPersonalWorkspace: boolean,
  workspace?: WorkspaceMinimalFragment,
  source?: string | null
) => {
  const isMAS = isAppStoreWorkspace(source)

  if (!hasWorkspaces || isGuest || !workspace) {
    return routes.PERSONAL_SETTINGS.create({})
  }

  if (isAdmin && !isPersonalWorkspace && !isMAS) {
    return routes.WORKSPACE_SETTINGS_SETTINGS.create({
      workspaceId: workspace.identifier,
    })
  }

  return routes.WORKSPACE_SETTINGS_PEOPLE.create({
    workspaceId: workspace.identifier,
  })
}

const AuthenticatedUserDropdown = (props: AuthenticatedUserDropdownProps) => {
  const { className, user, unreadNotifications, workspace } = props

  const { path } = useRouteMatch()

  const { data: signupSource } = useGetCustomerSignupSourceQuery({
    variables: { identifier: workspace?.identifier ?? '' }, // If there's no workspace, we skip the query
    skip: !workspace?.identifier,
  })

  const source = signupSource?.workspace?.customer?.source

  const { avatar, email, name } = user
  const isSuperAdmin = !!user?.isSuperadmin
  const isMobile = !useBreakpoint('xs')

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: DropdownContent,
    dropdownStyle: DROPDOWN_STYLE,
    dropdownProps: {
      email: email || '',
      name: name || '',
      unreadNotifications,
    },
    placement: 'top',
    hide: [
      'modalHide',
      'outsideViewport',
      'pressedEscape',
      'mouseLeave',
      ...(isMobile
        ? ([] as const)
        : (['clickedOutsideContent', 'clickedInsideContent'] as const)),
    ],
  })

  const hasWorkspaces = !!workspace
  const isGuest = workspace?.userRole === 'GUEST'
  const isAdmin = workspace?.userRole === 'ADMIN'
  const isPersonalWorkspace = workspace?.type === 'PERSONAL'

  const settingsRoute = determineSettingsRoute(
    hasWorkspaces,
    isGuest,
    isAdmin,
    isPersonalWorkspace,
    workspace,
    source
  )

  const isFinance = workspace?.userRole === 'FINANCE'
  const isPartner = workspace?.userRole === 'PARTNER'

  const showUpdateButton = !(isFinance || isPartner)

  // Agnostic workspace routes redirect to their respective workspace route
  // eg: workspace/settings/settings -> workspace/(id)/settings/settings
  // Due to this redirect we need to set the active state manually
  const isSettingsActive =
    isWorkspaceSettingsRoute(path) || isPersonalSettingsRoute(path)

  const isUpdatesActive = isUpdatesRoute(path)

  return (
    <SidebarHeaderRight>
      <NavLinkWrapper>
        <Tooltip
          content="Settings"
          placement="bottom"
          disabled={isSettingsActive}
        >
          <Button
            data-testid="user-settings"
            as={NavLink}
            to={settingsRoute}
            className={isSettingsActive ? 'active' : undefined}
          >
            <Icon width="24px" height="24px" as={GearIcon} />
            <span className="sr-only">Settings</span>
          </Button>
        </Tooltip>
        {showUpdateButton && (
          <Tooltip
            content="Updates"
            placement="bottom"
            disabled={isUpdatesActive}
            disableWhenTouchDevice
          >
            <Button as={NavLink} to={routes.UPDATES.create({})}>
              <Icon width="24px" height="24px" as={BellIcon} />
              <span className="sr-only">Updates</span>
              <NotificationsPill
                unreadNotifications={unreadNotifications}
                withNumberDisplay={false}
              />
            </Button>
          </Tooltip>
        )}
      </NavLinkWrapper>
      <Tooltip content="Your Account" placement="bottom">
        <Button
          className={className}
          data-testid="user-avatar"
          {...buttonProps}
        >
          <Avatar src={avatar!.small!} name={name!} size="24px" />
          {isSuperAdmin && (
            <KeyWrapper>
              <KeyIcon data-testid="super-admin-badge" width={8} />
            </KeyWrapper>
          )}
        </Button>
      </Tooltip>
      {content}
    </SidebarHeaderRight>
  )
}

const UnAuthenticatedUserDropdown = () => {
  const location = useLocation()

  return (
    <Button
      as={Link}
      to={{ pathname: routes.SIGN_IN.create({}), state: { from: location } }}
    >
      <Icon as={PersonIcon} />
      <UserName>Sign in</UserName>
    </Button>
  )
}

const PreventiveUserDropdown = (
  props: Partial<AuthenticatedUserDropdownProps>
) => {
  const { user, unreadNotifications, workspace } = props

  const content = !user ? (
    <UnAuthenticatedUserDropdown />
  ) : (
    <AuthenticatedUserDropdown
      user={user}
      unreadNotifications={unreadNotifications || 0}
      workspace={workspace}
    />
  )

  return <>{content}</>
}

const ConnectedUserDropdown: React.FCC = () => {
  const { data } = useUserProfile()
  const newNotificationsCount = useGetNewNotificationsCount({
    fetchPolicy: 'cache-only',
  })

  return (
    <PreventiveUserDropdown
      user={data?.me}
      unreadNotifications={newNotificationsCount}
    />
  )
}

export { ConnectedUserDropdown }
export default PreventiveUserDropdown
